import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  clearAuth,
  getProfile,
  isAdminUser,
  onAuthChange,
} from "../api/auth";

const AdminContext = createContext(null);

// idle: not checked
// loading: checking profile
// ready: verified admin
// denied: confirmed non-admin
// error: profile verification failed
export function AdminProvider({ children }) {
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [status, setStatus] = useState("idle");
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    return onAuthChange(() => {
      setCurrentAdmin(null);
      setAuthError("");
      setStatus("idle");
    });
  }, []);

  const loadAdmin = useCallback(async () => {
    setStatus("loading");
    setAuthError("");

    try {
      const response = await getProfile();
      const data = response?.data;

      if (isAdminUser(data)) {
        setCurrentAdmin({
          id: data.id,
          name: data.full_name || data.email || "Admin",
          email: data.email || "",
          isSuperuser: Boolean(data.is_superuser),
        });
        setStatus("ready");
      } else {
        setCurrentAdmin(null);
        setStatus("denied");
      }
    } catch (error) {
      setCurrentAdmin(null);

      const httpStatus = error?.response?.status;

      if (httpStatus === 401 || httpStatus === 403) {
        setStatus("denied");
        setAuthError("Your session is invalid or you do not have admin access. Please sign in again.");
      } else {
        setStatus("error");
        setAuthError(
          error?.response?.data?.detail ||
            error?.message ||
            "Unable to verify admin access. Check your connection and profile API."
        );
      }
    }
  }, []);

  const logout = useCallback(() => {
    clearAuth();
  }, []);

  const value = useMemo(
    () => ({
      currentAdmin,
      status,
      authError,
      loading: status === "loading",
      isAuthenticated: status === "ready",
      loadAdmin,
      logout,
    }),
    [currentAdmin, status, authError, loadAdmin, logout]
  );

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);

  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }

  return context;
}